import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    return {
      permissions: {},
      loading: true,
      isSuperAdmin: false,
      hasPermission: () => false,
      refreshPermissions: () => {},
    };
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions({});
      setLoading(false);
      return;
    }

    // Only load for admin panel users
    const adminRoles = ['super_admin', 'admin', 'manager', 'support'];
    if (!adminRoles.includes(user.role)) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const res = await adminAPI.getMyPermissions();
      setPermissions(res.data.permissions || {});
      setIsSuperAdmin(res.data.is_super_admin || false);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      // Default permissions on error
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has a specific permission
  // Usage: hasPermission('clients', 'edit') or hasPermission('clients.edit')
  const hasPermission = useCallback((section, permission) => {
    // Super admin has all permissions
    if (isSuperAdmin) return true;

    // Handle dot notation: 'clients.edit'
    if (!permission && section.includes('.')) {
      const [sec, perm] = section.split('.');
      return permissions[sec]?.[perm] === true;
    }

    // Handle separate params: ('clients', 'edit')
    return permissions[section]?.[permission] === true;
  }, [permissions, isSuperAdmin]);

  // Check if user has any permission in a section
  const hasSectionAccess = useCallback((section) => {
    if (isSuperAdmin) return true;
    const sectionPerms = permissions[section] || {};
    return Object.values(sectionPerms).some(v => v === true);
  }, [permissions, isSuperAdmin]);

  const refreshPermissions = useCallback(() => {
    setLoading(true);
    loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    loading,
    isSuperAdmin,
    hasPermission,
    hasSectionAccess,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
